// ----------------------------------------------------------------------
// <copyright file="Izenda.AutoresizeChart.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';

import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';

import { get, omit, has, flow, flatMap, isEmpty, isArrayLike, identity } from 'lodash/fp';
import { compose, branch, renderComponent, withProps } from 'recompose';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import ReportPartStyleFactories from 'share/factories/Izenda.ReportPartStyleFactories';

import GaugeChartActions from './Izenda.GaugeChartActions';
import PieChartActions from './Izenda.PieChartActions';

import SimpleChart from './Izenda.SimpleChart';
import NoData from 'share/components/controls/Izenda.NoData';
import ChartContainer from './Izenda.ChartContainer';
import SparklineChart from './Izenda.SparklineChart';
import GridChart from './Izenda.GridChart';

import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import Map from './Izenda.Map';

const isChartType = ReportPartStyleFactories.isReportPartStyle(REPORT_PART_TYPE.Chart);

/**
 * Render chart container with NoData component
 * @param {Object} props
 */
const NoDataChartContainer = props => (
  <ChartContainer {...props}>
    {props.children}

    <div className='chart-body'>
      <NoData finishedRender={props.finishedRender} />
    </div>
  </ChartContainer>
);

/**
 * Determine whether chart is not empty
 * @param {Object} chartData Chart data
 */
const hasData = chartData =>
  has('rawData', chartData) &&
  (isArrayLike(chartData.rawData)
    ? isEmpty(
        flatMap(
          flow(
            get('records'),
            toJS
          ),
          chartData.rawData
        )
      )
    : isEmpty(chartData.rawData.records));

/**
 * A HOC to render NoData when chart data is empty
 */
const WithNoData = compose(branch(({ chartData }) => hasData(chartData), renderComponent(NoData), identity));

/**
 * A HOC to render chart container with NoData when chart data is empty
 */
const WithNoDataChartContainer = compose(
  branch(({ chartData }) => hasData(chartData), renderComponent(NoDataChartContainer), identity)
);

/**
 * The Gauge chart types
 */
const GAUGE_CHARTS = [CHART_TYPE.SimpleGauge, CHART_TYPE.LinearGauge, CHART_TYPE.SolidGauge];

/**
 * The Pie chart types
 */
const PIE_CHARTS = [CHART_TYPE.Pie, CHART_TYPE.Donut, CHART_TYPE.Funnel, CHART_TYPE.TreeMap];

/**
 * Get Grid chart action by chart type
 * @param {string} chartType The rendering chart type
 */
const getGridAction = chartType => {
  switch (true) {
    case isChartType(chartType, ...GAUGE_CHARTS):
      return GaugeChartActions;
    case isChartType(chartType, ...PIE_CHARTS):
      return PieChartActions;
    default:
      return identity;
  }
};

const GridChartContainer = compose(
  withProps(props => getGridAction(props.chartType)(props)),
  WithNoDataChartContainer
)(GridChart);

/**
 * Get properties of each field which will be used to draw chart
 */
const getSeriesOptions = seriesOptions => {
  const seriesOpts = {};

  seriesOptions = seriesOptions || {};
  seriesOptions.map &&
    seriesOptions.forEach(serie => {
      const serieName = serie && serie.fieldName;

      if (!serieName) {
        return null;
      }

      seriesOpts[serieName] = ChartDataParser.getSerieProppertiesByField(serie);
    });

  return seriesOpts;
};

/**
 * Chart component to render all Charts, Gauges and Map
 */
@observer
export default class Chart extends Component {
  render() {
    const { reportPart, ...props } = this.props;
    const { isLoading, type, chartType, seriesOptions } = props;

    // Trick - this function will be used in render function to trigger react-mobx
    // re-render whenever field-element-prop is changed
    // get properties of each field which will be used to draw chart
    const seriesOptionsProps = getSeriesOptions(seriesOptions);

    switch (!isLoading) {
      case isChartType(chartType, ...[...GAUGE_CHARTS, ...PIE_CHARTS]):
        return <GridChartContainer {...props} seriesOptions={seriesOptionsProps} />;
      case isChartType(chartType, CHART_TYPE.Sparkline):
        return this.renderInChartContainer(WithNoData(SparklineChart), seriesOptionsProps);
      case type === REPORT_PART_TYPE.Map:
        return this.renderInChartContainer(Map, seriesOptionsProps);
      default:
        return this.renderInChartContainer(WithNoData(SimpleChart), seriesOptionsProps);
    }

    return null;
  }

  /**
   * Render component inside container
   * @param {Component} ChartComponent The chart component to be rendered inside container
   * @param {Object} seriesOptionsProps The series options
   */
  renderInChartContainer(ChartComponent, seriesOptionsProps) {
    const props = this.props;
    const { reportPart, style, ...chartProps } = props;
    const { printingMode, children, chartOptions, className } = chartProps;

    // Remove chart dimension in printing mode
    const chartStyle = printingMode ? omit(['height', 'width'], style) : style;

    return (
      <ChartContainer chartOptions={chartOptions} className={className} style={chartStyle} printingMode={printingMode}>
        {!printingMode && children}
        <div className='chart-body izenda-Wrapper-content'>
          <ChartComponent {...chartProps} style={chartStyle} seriesOptions={seriesOptionsProps} />
        </div>
      </ChartContainer>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.Chart.js